import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  GraduationCap, User, BookOpen, ArrowRight, ArrowLeft, Check,
  Lock, Mail, Phone, MapPin, Eye, EyeOff, CheckCircle, AlertCircle,
  Bot, Building2, ChevronRight, Bus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = 'https://customer-assets-eiarnc6j.emergentagent.net/job_uni-smartsys/artifacts/y8ickh5d_IMG-20260816-WA0087.jpg';

// ============ LOGIN PAGE ============
export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language } = useLanguage();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const l = language === 'fr' ? {
    welcome: 'Bienvenue',
    subtitle: 'Connectez-vous a votre espace academique',
    email: 'Adresse email',
    password: 'Mot de passe',
    login: 'Se connecter',
    noAccount: "Pas de compte ?",
    register: "S'inscrire",
    forgotPwd: 'Mot de passe oublie ?',
    tagline: 'La plateforme de gestion academique universitaire pour la RDC',
  } : {
    welcome: 'Welcome',
    subtitle: 'Sign in to your academic portal',
    email: 'Email address',
    password: 'Password',
    login: 'Sign in',
    noAccount: "Don't have an account?",
    register: 'Register',
    forgotPwd: 'Forgot password?',
    tagline: 'The university academic management platform for the DRC',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de connexion' : 'Login error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      {/* Left Panel - Branding (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A1628] relative flex-col justify-center items-center overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, #007BFF 0%, transparent 50%), radial-gradient(circle at 80% 20%, #007BFF 0%, transparent 40%)'
        }} />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(1px 1px at 20px 30px, rgba(255,255,255,0.15) 1px, transparent 0), radial-gradient(1px 1px at 40px 70px, rgba(255,255,255,0.1) 1px, transparent 0), radial-gradient(1px 1px at 60px 10px, rgba(255,255,255,0.12) 1px, transparent 0)',
          backgroundSize: '100px 100px'
        }} />

        <div className="relative z-10 text-center px-12 max-w-lg">
          <img src={LOGO_URL} alt="JoSmart Uni" className="w-48 h-48 mx-auto mb-8 rounded-3xl object-cover shadow-2xl" data-testid="login-logo-left" />
          <h1 className="text-3xl font-heading font-bold text-white mb-3">JoSmart Uni</h1>
          <p className="text-white/60 text-sm leading-relaxed">{l.tagline}</p>
          <div className="flex justify-center gap-6 mt-10 text-white/40 text-xs">
            <span>950+ Etablissements</span>
            <span>604K+ Etudiants</span>
            <span>Systeme LMD</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 sm:p-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={LOGO_URL} alt="JoSmart Uni" className="w-24 h-24 mx-auto mb-4 rounded-2xl object-cover shadow-lg" data-testid="login-logo-mobile" />
            <h1 className="text-2xl font-heading font-bold text-[#0F2C59]">JoSmart Uni</h1>
          </div>

          {/* Form Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
            <div className="mb-6">
              <h2 className="text-2xl font-heading font-bold text-slate-900">{l.welcome}</h2>
              <p className="text-sm text-slate-500 mt-1">{l.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-slate-700">{l.email}</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                    className="pl-11 h-11 border-slate-200 focus:border-[#007BFF] focus:ring-[#007BFF]"
                    placeholder="votre@email.cd"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">{l.password}</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                    className="pl-11 pr-11 h-11 border-slate-200 focus:border-[#007BFF] focus:ring-[#007BFF]"
                    required
                    data-testid="password-input"
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full h-11 bg-[#007BFF] hover:bg-[#0056b3] text-white font-medium rounded-lg shadow-sm" disabled={loading} data-testid="login-button">
                {loading ? <span className="animate-pulse">...</span> : l.login}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-slate-500">{l.noAccount} </span>
              <Link to="/register" className="text-[#007BFF] font-semibold hover:underline" data-testid="go-register-link">
                {l.register}
              </Link>
            </div>

            <div className="mt-4 text-center">
              <Link to="/legal" className="text-xs text-slate-400 hover:text-slate-600" data-testid="legal-link">
                {language === 'fr' ? 'Politique de confidentialite · Conditions d\'utilisation' : 'Privacy Policy · Terms of Use'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ REGISTRATION PAGE (AI-GUIDED) ============
export const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language } = useLanguage();

  // Step management
  const [step, setStep] = useState(0);
  const [userType, setUserType] = useState(null); // 'student', 'teacher', or 'driver'
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  // Form data
  const [form, setForm] = useState({
    university_code: '',
    promotion_code: '',
    teacher_id_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    password: '',
    confirm_password: '',
    language: 'fr'
  });
  const [showPwd, setShowPwd] = useState(false);

  const l = language === 'fr' ? {
    title: 'Inscription',
    aiGreeting: "Bienvenue sur JoSmart Uni ! Je suis votre assistant et je vais vous guider pour creer votre compte.",
    whoAreYou: "Qui etes-vous ?",
    student: 'Etudiant',
    studentDesc: "Je suis inscrit dans une universite",
    teacher: 'Enseignant',
    teacherDesc: "Je suis professeur ou vacataire",
    driver: 'Chauffeur',
    driverDesc: "Je suis chauffeur de bus universitaire",
    uniCode: "Code de votre universite",
    uniCodeDesc: "Ce code vous a ete communique par votre universite",
    promoCode: "Code de votre promotion",
    promoCodeDesc: "Exemple: L1-INFO-2025 (communique par votre chef de section)",
    teacherId: "Votre ID enseignant",
    teacherIdDesc: "Genere par l'administration de votre universite",
    verifying: "Verification en cours...",
    verified: "Verifie !",
    invalidCode: "Code invalide",
    personalInfo: "Vos informations personnelles",
    addressInfo: "Votre adresse",
    createPassword: "Creez votre mot de passe",
    pwdHint: "Minimum 6 caracteres",
    confirm: "Confirmer le mot de passe",
    register: "Creer mon compte",
    back: "Retour",
    next: "Continuer",
    haveAccount: "Deja un compte ?",
    login: "Se connecter",
    success: "Compte cree avec succes !",
    yourMatricule: "Votre matricule",
    firstName: "Prenom",
    lastName: "Nom de famille",
    email: "Adresse email",
    phone: "Telephone",
    address: "Adresse physique",
    city: "Ville",
    province: "Province",
    stepLabel: "Etape",
    of: "sur",
    aiCodeCheck: "Laissez-moi verifier vos codes d'acces...",
    aiPersonal: "Parfait ! Parlez-moi de vous.",
    aiAddress: "Ou habitez-vous ?",
    aiPassword: "Derniere etape : securisez votre compte.",
    aiDone: "Tout est pret ! Bienvenue dans JoSmart Uni.",
  } : {
    title: 'Registration',
    aiGreeting: "Welcome to JoSmart Uni! I'm your assistant and I'll guide you to create your account.",
    whoAreYou: "Who are you?",
    student: 'Student',
    studentDesc: "I'm enrolled in a university",
    teacher: 'Teacher',
    teacherDesc: "I'm a professor or part-time teacher",
    driver: 'Driver',
    driverDesc: "I'm a university bus driver",
    uniCode: "Your university code",
    uniCodeDesc: "This code was provided by your university",
    promoCode: "Your promotion code",
    promoCodeDesc: "Example: L1-INFO-2025 (from your section head)",
    teacherId: "Your teacher ID",
    teacherIdDesc: "Generated by your university administration",
    verifying: "Verifying...",
    verified: "Verified!",
    invalidCode: "Invalid code",
    personalInfo: "Your personal information",
    addressInfo: "Your address",
    createPassword: "Create your password",
    pwdHint: "Minimum 6 characters",
    confirm: "Confirm password",
    register: "Create my account",
    back: "Back",
    next: "Continue",
    haveAccount: "Already have an account?",
    login: "Sign in",
    success: "Account created successfully!",
    yourMatricule: "Your student ID",
    firstName: "First name",
    lastName: "Last name",
    email: "Email address",
    phone: "Phone",
    address: "Physical address",
    city: "City",
    province: "Province",
    stepLabel: "Step",
    of: "of",
    aiCodeCheck: "Let me verify your access codes...",
    aiPersonal: "Perfect! Tell me about yourself.",
    aiAddress: "Where do you live?",
    aiPassword: "Last step: secure your account.",
    aiDone: "All set! Welcome to JoSmart Uni.",
  };

  const totalSteps = userType === 'student' ? 5 : (userType === 'driver' ? 4 : 5);

  const aiMessages = [
    l.aiGreeting,
    l.aiCodeCheck,
    l.aiPersonal,
    userType === 'student' ? l.aiAddress : l.aiPassword,
    userType === 'student' ? l.aiPassword : l.aiDone,
    l.aiDone,
  ];

  // Verify codes
  const verifyCodes = async () => {
    if (!form.university_code) return;
    setVerifying(true);
    try {
      const params = new URLSearchParams({ university_code: form.university_code });
      if (userType === 'student' && form.promotion_code) params.append('promotion_code', form.promotion_code);
      if (userType === 'teacher' && form.teacher_id_code) params.append('teacher_id', form.teacher_id_code);

      const res = await axios.get(`${API_URL}/api/enrollment/verify?${params}`);
      setVerificationResult(res.data);
    } catch (err) {
      setVerificationResult({ valid: false, error: language === 'fr' ? 'Erreur de verification' : 'Verification error' });
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (step === 1 && form.university_code.length >= 4) {
      const timer = setTimeout(verifyCodes, 500);
      return () => clearTimeout(timer);
    }
  }, [form.university_code, form.promotion_code, form.teacher_id_code, step]);

  const canProceed = () => {
    switch (step) {
      case 0: return !!userType;
      case 1:
        if (!verificationResult?.valid) return false;
        if (userType === 'student') return verificationResult?.promotion_valid;
        if (userType === 'driver') return true; // driver only needs university code
        return verificationResult?.teacher_id_valid;
      case 2: return form.first_name && form.last_name && form.email;
      case 3:
        if (userType === 'student') return true; // address optional
        // driver and teacher go to password at step 3
        return form.password.length >= 6 && form.password === form.confirm_password;
      case 4: return form.password.length >= 6 && form.password === form.confirm_password;
      default: return false;
    }
  };

  const handleNext = () => {
    if (canProceed()) setStep(s => s + 1);
  };

  const handleRegister = async () => {
    if (form.password !== form.confirm_password) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone || null,
        role: userType === 'student' ? 'student' : userType === 'driver' ? 'driver' : 'teacher',
        university_code: form.university_code,
        language: form.language
      };
      if (userType === 'student') {
        payload.promotion_code = form.promotion_code;
        payload.address = form.address;
        payload.city = form.city;
        payload.province = form.province;
      } else if (userType === 'teacher') {
        payload.teacher_id_code = form.teacher_id_code;
      }

      const res = await axios.post(`${API_URL}/api/auth/register`, payload);
      toast.success(l.success);
      login(res.data);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0F2C59] to-[#1a3a6c] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#D4AF37]/20 flex items-center justify-center">
            <GraduationCap className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl font-bold text-white">JoSmart Uni</h1>
        </div>

        <Card className="border-0 shadow-2xl">
          <CardContent className="p-6">
            {/* Progress */}
            {step > 0 && (
              <div className="flex items-center gap-2 mb-4">
                {Array.from({ length: totalSteps }, (_, i) => (
                  <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${
                    i < step ? 'bg-[#0F2C59]' : i === step ? 'bg-[#D4AF37]' : 'bg-gray-200'
                  }`} />
                ))}
                <span className="text-xs text-gray-500 ml-2">{step}/{totalSteps}</span>
              </div>
            )}

            {/* AI Message */}
            <div className="flex items-start gap-3 mb-6 p-3 bg-blue-50 rounded-xl" data-testid="ai-guide-message">
              <div className="w-8 h-8 rounded-full bg-[#0F2C59] flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{aiMessages[step]}</p>
            </div>

            {/* Step 0: Choose type */}
            {step === 0 && (
              <div className="space-y-3" data-testid="step-choose-type">
                <p className="font-medium text-gray-900 text-center mb-4">{l.whoAreYou}</p>
                <button
                  onClick={() => setUserType('student')}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    userType === 'student' ? 'border-[#0F2C59] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="choose-student"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-[#0F2C59]" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{l.student}</p>
                    <p className="text-xs text-gray-500">{l.studentDesc}</p>
                  </div>
                  {userType === 'student' && <Check className="w-5 h-5 text-[#0F2C59] ml-auto" />}
                </button>
                <button
                  onClick={() => setUserType('teacher')}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    userType === 'teacher' ? 'border-[#0F2C59] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="choose-teacher"
                >
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-amber-700" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{l.teacher}</p>
                    <p className="text-xs text-gray-500">{l.teacherDesc}</p>
                  </div>
                  {userType === 'teacher' && <Check className="w-5 h-5 text-[#0F2C59] ml-auto" />}
                </button>
                <button
                  onClick={() => setUserType('driver')}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    userType === 'driver' ? 'border-[#0F2C59] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="choose-driver"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Bus className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{l.driver}</p>
                    <p className="text-xs text-gray-500">{l.driverDesc}</p>
                  </div>
                  {userType === 'driver' && <Check className="w-5 h-5 text-[#0F2C59] ml-auto" />}
                </button>
              </div>
            )}

            {/* Step 1: University + Promotion/Teacher codes */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-codes">
                <div>
                  <Label className="flex items-center gap-2"><Building2 className="w-4 h-4" />{l.uniCode}</Label>
                  <Input
                    value={form.university_code}
                    onChange={(e) => { setForm(p => ({ ...p, university_code: e.target.value.toUpperCase() })); setVerificationResult(null); }}
                    placeholder="DEMO-2026"
                    className="mt-1 font-mono"
                    data-testid="university-code-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{l.uniCodeDesc}</p>
                  {verificationResult?.valid && (
                    <div className="flex items-center gap-2 mt-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">{verificationResult.university_name}</span>
                    </div>
                  )}
                </div>

                {userType === 'student' && (
                  <div>
                    <Label>{l.promoCode}</Label>
                    <Input
                      value={form.promotion_code}
                      onChange={(e) => { setForm(p => ({ ...p, promotion_code: e.target.value.toUpperCase() })); setVerificationResult(null); }}
                      placeholder="L1-INFO-2025"
                      className="mt-1 font-mono"
                      data-testid="promotion-code-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">{l.promoCodeDesc}</p>
                    {verificationResult?.promotion_valid && (
                      <div className="flex items-center gap-2 mt-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">{verificationResult.promotion_label} - {verificationResult.program_name} ({verificationResult.academic_year})</span>
                      </div>
                    )}
                    {verificationResult && verificationResult.promotion_valid === false && (
                      <div className="flex items-center gap-2 mt-2 text-red-500">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">{l.invalidCode}</span>
                      </div>
                    )}
                  </div>
                )}

                {userType === 'teacher' && (
                  <div>
                    <Label>{l.teacherId}</Label>
                    <Input
                      value={form.teacher_id_code}
                      onChange={(e) => { setForm(p => ({ ...p, teacher_id_code: e.target.value.toUpperCase() })); setVerificationResult(null); }}
                      placeholder="ENS-DEMO-2026-0002"
                      className="mt-1 font-mono"
                      data-testid="teacher-id-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">{l.teacherIdDesc}</p>
                    {verificationResult?.teacher_id_valid && (
                      <div className="flex items-center gap-2 mt-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">{l.verified}</span>
                      </div>
                    )}
                    {verificationResult && verificationResult.teacher_id_valid === false && (
                      <div className="flex items-center gap-2 mt-2 text-red-500">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">{l.invalidCode}</span>
                      </div>
                    )}
                  </div>
                )}
                {verifying && <p className="text-xs text-blue-600 animate-pulse">{l.verifying}</p>}
              </div>
            )}

            {/* Step 2: Personal info */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-personal">
                <p className="font-medium text-gray-700">{l.personalInfo}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{l.firstName}</Label>
                    <Input value={form.first_name} onChange={(e) => setForm(p => ({ ...p, first_name: e.target.value }))} data-testid="reg-firstname" />
                  </div>
                  <div>
                    <Label>{l.lastName}</Label>
                    <Input value={form.last_name} onChange={(e) => setForm(p => ({ ...p, last_name: e.target.value }))} data-testid="reg-lastname" />
                  </div>
                </div>
                <div>
                  <Label>{l.email}</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} data-testid="reg-email" />
                </div>
                <div>
                  <Label>{l.phone}</Label>
                  <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+243 xxx xxx xxx" data-testid="reg-phone" />
                </div>
              </div>
            )}

            {/* Step 3: Address (student) or Password (teacher) */}
            {step === 3 && userType === 'student' && (
              <div className="space-y-4" data-testid="step-address">
                <p className="font-medium text-gray-700 flex items-center gap-2"><MapPin className="w-4 h-4" />{l.addressInfo}</p>
                <div>
                  <Label>{l.address}</Label>
                  <Input value={form.address} onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Av. de la Paix, N.25" data-testid="reg-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{l.city}</Label>
                    <Input value={form.city} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))} placeholder="Kinshasa" data-testid="reg-city" />
                  </div>
                  <div>
                    <Label>{l.province}</Label>
                    <Input value={form.province} onChange={(e) => setForm(p => ({ ...p, province: e.target.value }))} placeholder="Kinshasa" data-testid="reg-province" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 (teacher/driver) or Step 4 (student): Password */}
            {((step === 3 && (userType === 'teacher' || userType === 'driver')) || (step === 4 && userType === 'student')) && (
              <div className="space-y-4" data-testid="step-password">
                <p className="font-medium text-gray-700 flex items-center gap-2"><Lock className="w-4 h-4" />{l.createPassword}</p>
                <div>
                  <Label>{l.createPassword}</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPwd ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                      className="pr-10"
                      data-testid="reg-password"
                    />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{l.pwdHint}</p>
                </div>
                <div>
                  <Label>{l.confirm}</Label>
                  <Input
                    type="password"
                    value={form.confirm_password}
                    onChange={(e) => setForm(p => ({ ...p, confirm_password: e.target.value }))}
                    data-testid="reg-confirm-password"
                  />
                  {form.confirm_password && form.password !== form.confirm_password && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match'}
                    </p>
                  )}
                  {form.confirm_password && form.password === form.confirm_password && form.password.length >= 6 && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {language === 'fr' ? 'Mots de passe identiques' : 'Passwords match'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              {step > 0 ? (
                <Button variant="ghost" onClick={() => setStep(s => s - 1)} className="gap-2" data-testid="reg-back-button">
                  <ArrowLeft className="w-4 h-4" /> {l.back}
                </Button>
              ) : (
                <div />
              )}

              {((step === 4 && userType === 'student') || (step === 3 && (userType === 'teacher' || userType === 'driver'))) ? (
                <Button
                  onClick={handleRegister}
                  disabled={loading || !canProceed()}
                  className="gap-2 bg-[#0F2C59] hover:bg-[#0F2C59]/90"
                  data-testid="register-submit-button"
                >
                  {loading ? '...' : l.register} <Check className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="gap-2 bg-[#0F2C59] hover:bg-[#0F2C59]/90"
                  data-testid="reg-next-button"
                >
                  {l.next} <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="mt-4 text-center text-sm">
              <span className="text-gray-500">{l.haveAccount} </span>
              <Link to="/login" className="text-[#0F2C59] font-medium hover:underline" data-testid="go-login-link">
                {l.login}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
